import { useState, useEffect, useCallback } from "react";
import { Alert, Box, CircularProgress, Divider, IconButton, Snackbar, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useApi } from "../../hooks/useApi";
import { BillingProfileResponse } from "../../types/billing";
import { BRAND_LABEL } from "../../constants/brand";
import CompanyBillingProfilePanel from "./CompanyBillingProfilePanel";
import AddBillingProfileDialog from "./AddBillingProfileDialog";
import RenameBillingProfileDialog from "./RenameBillingProfileDialog";
import AddAccountButton from "./AddAccountButton";

interface Props {
  companyId: string;
}

export default function CompanyBillingManager({ companyId }: Props) {
  const { get } = useApi();

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [billingProfiles, setBillingProfiles] = useState<BillingProfileResponse[]>([]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [addOpen, setAddOpen] = useState(false);
  const [renaming, setRenaming] = useState<BillingProfileResponse | null>(null);

  const fetchBilling = useCallback(async () => {
    setFetchError(null);
    setLoading(true);
    try {
      const response = await get<BillingProfileResponse[]>(`/api/companies/${companyId}/billing`);
      setBillingProfiles(response);
    } catch (err: any) {
      setFetchError(err.message || "Failed to load billing info");
    } finally {
      setLoading(false);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const notify = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  if (loading) {
    return (
      <div>
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end gap-2">
        <AddAccountButton label="+ Billing profile" onClick={() => setAddOpen(true)} />
      </div>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>
      )}

      {billingProfiles.map((billingProfile, idx) => (
        <Box key={billingProfile.billingProfileId}>
          {idx > 0 && <Divider sx={{ my: 5 }} />}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>{billingProfile.billingProfileName}</Typography>
              <IconButton size="small" aria-label="Rename billing profile" onClick={() => setRenaming(billingProfile)}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {billingProfile.brands.length
                ? `${BRAND_LABEL.plural}: ${billingProfile.brands.map((b) => b.name).join(", ")}`
                : `No ${BRAND_LABEL.plural.toLowerCase()}`}
            </Typography>
          </Box>
          <CompanyBillingProfilePanel
            companyId={companyId}
            billingProfile={billingProfile}
            onChanged={fetchBilling}
            onNotify={notify}
          />
        </Box>
      ))}

      <AddBillingProfileDialog
        open={addOpen}
        companyId={companyId}
        onClose={() => setAddOpen(false)}
        onCreated={async (billingProfile) => {
          setAddOpen(false);
          await fetchBilling();
          notify(`Billing profile "${billingProfile.name}" added`, "success");
        }}
      />

      {renaming && (
        <RenameBillingProfileDialog
          billingProfile={renaming}
          companyId={companyId}
          onClose={() => setRenaming(null)}
          onRenamed={async (name) => {
            setRenaming(null);
            await fetchBilling();
            notify(`Billing profile renamed to "${name}"`, "success");
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
